import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchProducts } from '../redux/user/shop/shop_service_syn';
import Sidebar from './AsideComponent/Sidebar';
import LoaderScreen from '../component/loader/loaderScreen';
import {
  UserCartCreation,
  UserGetCart,
} from '../redux/user/cart/cartServicesSync';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaHeart, FaShoppingCart } from 'react-icons/fa';
import { InfinitySpin } from 'react-loader-spinner';
import { updateCartIndicator } from '../redux/user/cart/cartSlices';
import {
  UserWishListCreation,
  UserGetWishList,
} from '../redux/user/wish_list/wish_list_services_sync';
import {
  updateProductBrand,
  updateProductCategory,
  updateProductName,
  updateProductPage,
} from '../redux/user/shop/shopSlice';
import PaginationComponent from '../component/pagination/PaginationComponent';

const ProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartIndicator } = useSelector((state) => state.user_cart);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const { wish_list } = useSelector((state) => state.user_wish_list);

  const isInWishlist = useMemo(() => {
    return wish_list?.products?.some((item) => item.product === product._id);
  }, [wish_list, product._id]);

  const handleAddToCart = () => {
    dispatch(updateCartIndicator(product._id));

    dispatch(
      UserCartCreation({
        productId: product._id,
        quantity: 1,
        price: product.price,
      })
    )
      .then(() => {
        dispatch(UserGetCart());
        dispatch(updateCartIndicator(''));
      })
      .catch((error) => {
        console.log(error);
        dispatch(updateCartIndicator(''));
      });
  };

  const handleViewProduct = () => {
    navigate(`/product/${product._id}`);
  };

  const handleAddToWishlist = () => {
    setWishlistLoading(true);
    dispatch(
      UserWishListCreation({
        productId: product._id,
        price: product.price,
      })
    )
      .then(() => {
        dispatch(UserGetWishList());
        setWishlistLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setWishlistLoading(false);
      });
  };

  return (
    <div className="col mb-30">
      <div className="product__items h-full">
        <div className="product__items--thumbnail h-[240px] overflow-hidden">
          <a
            className="product__items--link block h-full"
            href={`/product/${product._id}`}
          >
            <img
              className="product__items--img product__primary--img w-full h-full object-cover"
              src={product.gallery?.[0]?.pic_url}
              alt={product.product_name}
            />
          </a>
        </div>
        {/* Quick actions */}
        <div className="absolute right-8 top-4 flex flex-col gap-2">
          <button
            className="p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
            onClick={handleViewProduct}
          >
            <FaEye className="w-5 h-5" />
          </button>
          <button
            className="p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
            onClick={handleAddToWishlist}
            disabled={wishlistLoading}
          >
            <FaHeart
              className={`w-5 h-5 ${
                isInWishlist ? 'text-red-500' : 'text-gray-400'
              }`}
            />
          </button>
        </div>
        <div className="product__items--content text-center bg-white p-4">
          <h3 className="product__items--content__title h4 text-lg line-clamp-2 min-h-[1.2rem]">
            <a href={`/product/${product._id}`}>{product.product_name}</a>
          </h3>
          <div className="product__items--price my-3 flex gap-3">
            <span className="current__price font-bold">
              {product.symbol}
              {product.price}
            </span>
            <span className="text-gray-500 font-bold line-through lg:mt-1">
              {product.symbol}
              {product.discount}
            </span>
          </div>
          <button
            className="product__items--action__cart--btn primary__btn block w-full"
            onClick={handleAddToCart}
          >
            {product._id === cartIndicator ? (
              <InfinitySpin width="80" color="#FFFFFF" />
            ) : (
              <>
                <FaShoppingCart className="w-5 h-5 flex items-center justify-center mt-[0.9rem] mr-4" />
                Add to cart
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const AsideShopComponent = () => {
  const { profile } = useSelector((state) => state.user_profile);

  const dispatch = useDispatch();
  const { products, loading, product_page, product_limit, totalProducts } =
    useSelector((state) => state.shop);
  const productsList = useMemo(() => products?.docs || [], [products]);
  const [searchTerm, setSearchTerm] = useState(''); // Add this line
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [priceRange, setPriceRange] = useState({ from: 0, to: 0 });

  useEffect(() => {
    if (profile) {
      dispatch(UserGetCart());
      dispatch(UserGetWishList());
    }

    dispatch(
      searchProducts({
        name: searchTerm || '""',
        category: selectedCategory || '""',
        brand: selectedBrand || '""',
        from: priceRange.from || 0,
        to: priceRange.to || 0,
        page: product_page,
        limit: product_limit,
      })
    );

    return () => {
      dispatch(updateProductPage(0));
      dispatch(updateProductName(''));
      dispatch(updateProductCategory(''));
      dispatch(updateProductBrand(''));
    };
  }, []);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    // Reset brand when category changes
    setSelectedBrand('');
  };

  const handleBrandSelect = (brandId) => {
    setSelectedBrand(brandId);
    // Reset category when brand changes
    setSelectedCategory('');
  };

  const handlePageChange = (page) => {
    dispatch(
      searchProducts({
        name: searchTerm || '""',
        category: selectedCategory || '""',
        brand: selectedBrand || '""',
        from: priceRange.from || 0,
        to: priceRange.to || 0,
        page: page,
        limit: product_limit,
      })
    );
  };

  return (
    <section className="shop__section section--padding">
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-3 col-lg-4">
            <Sidebar
              onCategorySelect={handleCategorySelect}
              onBrandSelect={handleBrandSelect}
              onPriceChange={setPriceRange}
              searchTerm={searchTerm} // Pass the search term to Sidebar
            />
          </div>

          <div className="col-xl-9 col-lg-8">
            <div className="shop__header bg__gray--color d-flex align-items-center justify-content-between mb-30">
              <p className="product__showing--count">
                Showing {totalProducts || 0} products
              </p>
              {/* Display active filters */}
              <div className="flex gap-2">
                {selectedCategory && (
                  <div className="bg-gray-200 px-3 py-1 rounded-full flex items-center gap-1">
                    <span>Category: {selectedCategory}</span>
                    <button
                      onClick={() => setSelectedCategory('')}
                      className="text-gray-500 hover:text-red-500"
                    >
                      x
                    </button>
                  </div>
                )}
                {selectedBrand && (
                  <div className="bg-gray-200 px-3 py-1 rounded-full flex items-center gap-1">
                    <span>Brand</span>
                    <button
                      onClick={() => setSelectedBrand('')}
                      className="text-gray-500 hover:text-red-500"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="shop__product--wrapper">
              <div className="product__section--inner product__grid--inner">
                <div className="row row-cols-xl-3 row-cols-lg-3 row-cols-md-2 row-cols-1 mb--n30">
                  {loading ? (
                    <div
                      className="col-12 d-flex justify-content-center align-items-center"
                      style={{ minHeight: '50vh' }}
                    >
                      <LoaderScreen height="50vh" />
                    </div>
                  ) : productsList?.length > 0 ? (
                    productsList.map((product) => (
                      <ProductCard key={product._id} product={product} />
                    ))
                  ) : (
                    <p>No products found</p>
                  )}
                </div>

                <PaginationComponent
                  currentPage={products?.page}
                  totalItems={products?.totalDocs}
                  pageSize={product_limit}
                  handlePageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AsideShopComponent;
